import { z } from 'zod';
import { TimestampSchema } from './common.js';

/**
 * The live stream (doc 05 §8): one WebSocket per browser tab, channels inside it.
 *
 * Event-driven end to end (owner, 15 September 2026). The switch's own events
 * reach arc-agent, the agent pushes the moment something changes, arc-api fans
 * the change out on the channels it belongs to, and the browser renders it.
 * Nothing on the path asks "anything new?" on a timer; the only timers are
 * heartbeats that prove a quiet connection is still alive, and a slow
 * consistency sweep on the node that catches an event the switch never sent.
 *
 * Authentication is a TICKET: minted by an authenticated REST call, single use,
 * valid for REALTIME_TICKET_TTL_SEC. A browser cannot put an Authorization
 * header on a WebSocket, and a long-lived token in a URL ends up in logs; a
 * ticket that dies on first use is worthless there.
 */

export const REALTIME_TICKET_TTL_SEC = 30;
/** The server sends a heartbeat this often, and a WebSocket ping with it. */
export const REALTIME_HEARTBEAT_MS = 20_000;
/** A client that hears nothing at all for this long reconnects. */
export const REALTIME_SILENCE_MS = 45_000;
/** Channels one connection may hold. */
export const REALTIME_MAX_CHANNELS = 50;

/** Close codes the client acts on. 4001 and 4002 mean mint a new ticket; 4003 means stop. */
export const REALTIME_CLOSE = {
  /** Ticket missing, used, expired or never issued. */
  unauthorized: 4001,
  /** The session behind the ticket was revoked, or its permissions changed. */
  sessionEnded: 4002,
  /** The Origin header is not the portal's. Retrying cannot help. */
  originRefused: 4003,
  /** The connection fell too far behind; reconnecting gets a fresh snapshot. */
  slowConsumer: 4008,
} as const;

export const RealtimeTicketSchema = z.object({
  ticket: z.string().min(32).max(128),
  expires_at: TimestampSchema,
});
export type RealtimeTicket = z.infer<typeof RealtimeTicketSchema>;

/**
 * What a channel carries.
 *
 * calls          who is ringing or on a call (a payload: it changes several times a call)
 * queues         callers waiting and agents available (a payload)
 * registrations  a nudge that presence changed; the client refetches /v1/live/presence,
 *                so registration rows still have one code path and one permission
 * shifts         a nudge that somebody logged on, paused or logged off (0107). A nudge for the
 *                same reason: attendance is a report with a permission and a shape of its own,
 *                and pushing the numbers would be a second way to compute them
 */
export const REALTIME_TOPICS = ['calls', 'queues', 'registrations', 'shifts'] as const;
export type RealtimeTopic = (typeof REALTIME_TOPICS)[number];

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const CHANNEL_RE = new RegExp(`^(?:tenant:(${UUID})|platform):(calls|queues|registrations|shifts)$`);

/** `tenant:<tenant uuid>:<topic>`, or `platform:<topic>` for every tenant at once. */
export const RealtimeChannelSchema = z.string().max(64).regex(CHANNEL_RE);

export const tenantChannel = (tenantId: string, topic: RealtimeTopic): string =>
  `tenant:${tenantId}:${topic}`;
export const platformChannel = (topic: RealtimeTopic): string => `platform:${topic}`;

/** Null for anything that is not a channel name. `tenantId` is null for a platform channel. */
export function parseChannel(channel: string): { tenantId: string | null; topic: RealtimeTopic } | null {
  const m = CHANNEL_RE.exec(channel);
  if (!m) return null;
  return { tenantId: m[1] ?? null, topic: m[2] as RealtimeTopic };
}

// ------------------------------------------------------------------ payloads

export const LiveExtensionSchema = z.object({
  user: z.string(),
  domain: z.string(),
  state: z.enum(['idle', 'ringing', 'on_call']),
  /** Epoch ms of the transition into this state; the page counts up from it. */
  since: z.number().int(),
  direction: z.enum(['inbound', 'outbound', 'internal']).nullable(),
  peer: z.string().nullable(),
  /** Present on platform channels, where entries from every tenant are mixed. */
  tenant_id: z.string().uuid().optional(),
});
export type LiveExtension = z.infer<typeof LiveExtensionSchema>;

/**
 * One agent in a queue, as mod_callcenter holds them (30 September 2026). Every agent tiered into
 * the queue is listed, logged out or not, so the roster is the queue's staff and not just who is on.
 * `status` and `state` are the switch's own words; the page turns them into one of its own.
 */
export const LiveQueueAgentSchema = z.object({
  /** The extension number. */
  agent: z.string().max(64),
  /** Available, Available (On Demand), On Break, Logged Out. */
  status: z.string().max(40),
  /** Waiting, Receiving, In a queue call, Idle, Reserved. */
  state: z.string().max(40),
  level: z.number().int(),
  position: z.number().int(),
  /** Epoch ms: when the queue call started while on one, else the last status change. */
  since: z.number().int().nullable(),
  /** Epoch ms until which the agent will not be offered a call (wrap-up and delays); null when free. */
  ready_at: z.number().int().nullable(),
});
export type LiveQueueAgent = z.infer<typeof LiveQueueAgentSchema>;

export const LiveQueueSchema = z.object({
  queue: z.string(),
  name: z.string(),
  domain: z.string(),
  waiting: z.number().int().min(0),
  agents_total: z.number().int().min(0),
  agents_available: z.number().int().min(0),
  agents_on_call: z.number().int().min(0),
  calls_answered: z.number().int().min(0),
  /** As it was when the node last read the queue. Use longest_waiting_since to show it. */
  longest_wait_sec: z.number().int().min(0),
  /**
   * Epoch ms the longest-waiting caller joined, or null when nobody waits.
   * The page counts up from this, so a caller waiting needs no push per second.
   */
  longest_waiting_since: z.number().int().nullable(),
  /** The queue's agents (30 September 2026). Absent from a node that predates it. */
  agents: z.array(LiveQueueAgentSchema).max(500).optional(),
  tenant_id: z.string().uuid().optional(),
});
export type LiveQueue = z.infer<typeof LiveQueueSchema>;

/**
 * Every update is the channel's whole current view, not a diff: a missed message
 * then costs one stale frame, never a permanently wrong screen. `nodes` is how
 * many nodes reported recently enough to be believed; 0 means nothing is live.
 */
export const LiveCallsDataSchema = z.object({
  extensions: z.array(LiveExtensionSchema),
  nodes: z.number().int().min(0),
});
export type LiveCallsData = z.infer<typeof LiveCallsDataSchema>;

export const LiveQueuesDataSchema = z.object({
  queues: z.array(LiveQueueSchema),
  nodes: z.number().int().min(0),
});
export type LiveQueuesData = z.infer<typeof LiveQueuesDataSchema>;

// ------------------------------------------------------------------ messages

export const RealtimeClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('subscribe'),
    channels: z.array(RealtimeChannelSchema).min(1).max(REALTIME_MAX_CHANNELS),
  }).strict(),
  z.object({
    type: z.literal('unsubscribe'),
    channels: z.array(RealtimeChannelSchema).min(1).max(REALTIME_MAX_CHANNELS),
  }).strict(),
]);
export type RealtimeClientMessage = z.infer<typeof RealtimeClientMessageSchema>;

/**
 * ready       the ticket was accepted
 * subscribed  these channels are now held (a refused channel gets an error instead)
 * snapshot    a calls or queues channel's view, sent once on subscribing
 * update      the same, sent whenever it changes
 * nudge       a registrations channel: presence changed, refetch it
 * heartbeat   nothing changed, and the connection is alive
 * error       a request was refused; `channel` says which one when there is one
 */
export const RealtimeServerMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ready'), at: TimestampSchema }),
  z.object({ type: z.literal('subscribed'), channels: z.array(z.string()) }),
  z.object({
    type: z.literal('snapshot'), channel: z.string(), at: TimestampSchema,
    data: z.union([LiveCallsDataSchema, LiveQueuesDataSchema]),
  }),
  z.object({
    type: z.literal('update'), channel: z.string(), at: TimestampSchema,
    data: z.union([LiveCallsDataSchema, LiveQueuesDataSchema]),
  }),
  z.object({ type: z.literal('nudge'), channel: z.string(), at: TimestampSchema }),
  z.object({ type: z.literal('heartbeat'), at: TimestampSchema }),
  z.object({
    type: z.literal('error'), code: z.string(), message: z.string(),
    channel: z.string().optional(),
  }),
]);
export type RealtimeServerMessage = z.infer<typeof RealtimeServerMessageSchema>;
